import { StoryblokVue, apiPlugin, type ISbStoryParams } from '@storyblok/vue'
import { defineNuxtPlugin, useRuntimeConfig } from '#app'
import type { InjectionKey } from 'vue'

// The storyblok plugin doesn't offer its own proper type for the vue plugin.
interface StoryblokPluginSettings {
  accessToken: string
  bridge?: boolean
  useApiClient?: boolean
  version?: 'draft' | 'published'
}

export type StoryblokSettings = Required<StoryblokPluginSettings> & {
  apiSettings: Partial<ISbStoryParams>
}

// We can't use Symbol for injectionKeys anymore, because nuxt copies the plugin code to the server entry point for compilation.
// The SSR code that then actually gets run executes this code though, so we would have 2 different instantiations of "Symbol()".
export const storyblokSettingsKey = 'storyblokSettings' as unknown as InjectionKey<StoryblokSettings>

export default defineNuxtPlugin({
  name: 'storyblok',
  async setup({ vueApp }) {
    const { storyblok } = useRuntimeConfig().public

    let token: string
    let accessLevel: string
    if (import.meta.server) {
      const { environment, host, path, headers } = usePageEnvironment()
      const config = getPageConfig(environment, host, path, headers)
      if (config === undefined) {
        // some pages like health check don't need any storyblok and should be callable as any IP or domain.
        return
      }
      token = useRuntimeConfig()[config.storyblok.tokenNuxtRuntimeName] as string
      accessLevel = config.storyblok.accessLevel
    } else {
      // storyblokAccessToken gets written by the storyblok nitro plugin.
      token = useRuntimeConfig().public.storyblokAccessToken
      accessLevel = (await usePageConfig()).storyblok.accessLevel
    }

    // happens often enough for new systems, that we need a more specific error
    if (!token) {
      let tokenName = 'NUXT_CURABOX_PUBLISHED_ACCESS_TOKEN'
      if (accessLevel === 'draft') {
        tokenName = 'NUXT_CURABOX_DRAFT_ACCESS_TOKEN'
      }

      throw new Error(`Storyblok token is not set. Set ${tokenName} in your .env file.`)
    }

    const params: StoryblokPluginSettings = {
      ...storyblok,
      accessToken: token,
    }
    if (accessLevel === 'draft') {
      params.bridge = true
      params.useApiClient = true
      params.version = 'draft'
    } else {
      params.bridge = false
      params.useApiClient = false
      params.version = 'published'
    }

    vueApp.provide(storyblokSettingsKey, {
      ...params as Required<StoryblokPluginSettings>,
      apiSettings: {
        version: params.version,
        token: params.accessToken,
      },
    })
    vueApp.use(StoryblokVue, { ...params, use: [apiPlugin] })
  },
})
